/**
 * Upload Recovery Service
 * Detects and recovers incomplete uploads from IndexedDB after page refresh
 */

import { db } from '../idb/chunk-db';
import logger from '../utils/logger';

export interface PendingUpload {
  sceneId: string;
  videoGuid: string;
  chunkCount: number;
  totalSize: number;
  oldestChunkTime: number;
}

/**
 * Detect scenes with pending chunks (incomplete uploads)
 * Returns map of sceneId -> pending upload info
 */
export async function detectPendingUploads(): Promise<Map<string, PendingUpload>> {
  try {
    // Get all chunks from IndexedDB
    const allChunks = await db.chunks.toArray();
    
    if (allChunks.length === 0) {
      logger.info('[UploadRecovery] No chunks found in IndexedDB');
      return new Map();
    }
    
    // Group chunks by sceneId (only chunks that have sceneId)
    const chunksByScene = new Map<string, typeof allChunks>();
    
    for (const chunk of allChunks) {
      if (chunk.sceneId) {
        if (!chunksByScene.has(chunk.sceneId)) {
          chunksByScene.set(chunk.sceneId, []);
        }
        chunksByScene.get(chunk.sceneId)!.push(chunk);
      }
    }
    
    // Build pending upload info for each scene
    const pendingUploads = new Map<string, PendingUpload>();
    
    // Convert entries to array to avoid iterator issues
    const entries = Array.from(chunksByScene.entries());
    
    for (const [sceneId, chunks] of entries) {
      const chunkCount = chunks.length;
      const totalSize = chunks.reduce((sum: number, chunk) => sum + chunk.size, 0);
      const oldestChunkTime = Math.min(...chunks.map((c) => c.createdAt));
      
      // Use the videoGuid from the first chunk (all chunks for a scene should have the same videoGuid)
      const videoGuid = chunks[0].videoGuid;
      
      pendingUploads.set(sceneId, {
        sceneId,
        videoGuid,
        chunkCount,
        totalSize,
        oldestChunkTime,
      });
      
      logger.info(`[UploadRecovery] Found pending upload for scene ${sceneId}:`, {
        videoGuid,
        chunkCount,
        totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
        age: ((Date.now() - oldestChunkTime) / 1000 / 60).toFixed(1) + ' minutes',
      });
    }
    
    if (pendingUploads.size > 0) {
      logger.info(`[UploadRecovery] ✅ Detected ${pendingUploads.size} scene(s) with pending uploads`);
    }
    
    return pendingUploads;
  } catch (error) {
    logger.error('[UploadRecovery] Error detecting pending uploads:', error);
    return new Map();
  }
}

/**
 * Clean up chunks for a specific scene (after successful upload)
 */
export async function cleanupSceneChunks(sceneId: string): Promise<void> {
  try {
    const chunks = await db.chunks.toArray();
    const sceneChunks = chunks.filter(c => c.sceneId === sceneId);
    
    if (sceneChunks.length > 0) {
      await Promise.all(sceneChunks.map(chunk => db.chunks.delete(chunk.id)));
      logger.info(`[UploadRecovery] Cleaned up ${sceneChunks.length} chunks for scene ${sceneId}`);
    }
  } catch (error) {
    logger.error(`[UploadRecovery] Error cleaning up chunks for scene ${sceneId}:`, error);
  }
}
